import { Randomize, Reset } from "assets/icons/Common";
import classNames from "classnames";
import { Col, IconButtonInverted, Row } from "components/primitives";
import { lerp } from "components/utils";
import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { WidgetFC } from "types/Widget";

export interface InteractiveRandomNameState {
  displayName: string;
  remainingNames: string[];
  usedNames: string[];
}

export const InteractiveRandomName: WidgetFC<InteractiveRandomNameState> = ({
  state: { displayName, remainingNames, usedNames },
  setState,
  settings,
  widgetId,
}) => {
  const { nameList, isSequential } = settings;
  const cleanNameList: string[] =
    nameList?.split("\n").filter((lineItem: string) => Boolean(lineItem)) || [];
  const [playAnim, setPlayAnim] = useState(false);
  const [isRandomising, setIsRandomising] = useState(false);

  const reset = () => {
    setState({
      remainingNames: [...cleanNameList],
      usedNames: [],
      displayName: "...",
    });
  };

  const getNext = (remainingList: string[], isSequential: boolean): number => {
    if (remainingList.length === 0) {
      reset(); // Reset if the remaining list is empty
    }

    if (isSequential) {
      return 0;
    } else {
      return Math.floor(Math.random() * remainingList.length);
    }
  };

  const randomiseName = () => {
    setIsRandomising(true);

    setTimeout(() => {
      let remainingList = remainingNames.length ? [...remainingNames] : [...cleanNameList];
      let usedList = [...usedNames];
      let nextName = "";

      const nextIndex = getNext(remainingList, isSequential);

      if (isSequential) {
        // Sequential: Use the first name in the list
        nextName = remainingList.shift() || ""; // Remove the first name
      } else {
        // Non-sequential: Pick a random name
        nextName = remainingList[nextIndex];
        remainingList.splice(nextIndex, 1); // Remove the selected name
      }

      // Add the name to `usedNames`
      if (nextName) {
        usedList.push(nextName);
      }

      // Update the state
      setState({
        displayName: nextName,
        remainingNames: [...remainingList],
        usedNames: [...usedList],
      });

      doPlayAnim();
      setIsRandomising(false);
    }, 1);
  };

  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only reset if `isSequential` changes and `widgetId` hasn't changed
    if (widgetIdRef.current === widgetId) {
      reset();
    }
    // Update the `widgetIdRef` to the current `widgetId` after the effect runs
    widgetIdRef.current = widgetId;
  }, [isSequential, widgetId]);

  const doPlayAnim = () => {
    setPlayAnim(true);
    setTimeout(() => {
      setPlayAnim(false);
    }, 1000);
  };

  const fontSize = lerp(18, 6, displayName.length / 20);

  return (
    <OuterContainer>
      <TopBanner />
      <NameText fontSize={`${fontSize}cqw`} playAnim={playAnim}>
        {displayName}
      </NameText>
      <BottomBanner>
        <Row style={{ gap: 10 }}>
          <GoButton
            withShadow={false}
            Icon={Randomize}
            onClick={randomiseName}
            disabled={cleanNameList.length === 0 || isRandomising}
          />
          <GoButton
            withShadow={false}
            Icon={Reset}
            onClick={reset}
            disabled={cleanNameList.length === 0 || isRandomising}
          />
        </Row>
      </BottomBanner>
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
`;

const NameText = styled.p.attrs<{ playAnim: boolean }>(({ playAnim }) => ({
  className: classNames("animate__animated", playAnim && "animate__tada"),
}))<{ playAnim: boolean; fontSize: string }>`
  font-size: ${(props) => props.fontSize};
  height: 15cqw;
  line-height: 15cqw;
`;

const Banner = styled.div`
  background-color: #eaf0ff;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TopBanner = styled(Banner)`
  border-radius: var(--widget-border-radius) var(--widget-border-radius) 0 0;
  height: 15cqh;
`;

const BottomBanner = styled(Banner)`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0 0 var(--widget-border-radius) var(--widget-border-radius);
  height: 20cqh;
  width: 100%;
`;

const GoButton = styled(IconButtonInverted)`
  width: 15cqh;
  height: 15cqh;
`;
